import { IColumn, IconButton, Stack, Text, TooltipHost } from '@fluentui/react';
import { GenericAttachForm } from 'common/components/ActionMenu/Attach/AttachFormModal';
import { useStyles } from 'common/components/ActionMenu/Attach/AttachFormModal/index.styles';
import { DocumentDataCallout } from 'common/components/AttachDocumentModal/DocumentDataCallout';
import { DocumentViewModalState } from 'common/components/DocumentList';
import { TABLE_ROWS } from 'common/constants';
import {
  dateConvertions,
  dateFormat,
  getGlobalDateFormat,
} from 'common/utils/dateFormats';
import React, { useCallback } from 'react';
import { toOrderByVariable } from '../utils';
import {
  AttachableSupplierDocuments,
  AttachableSupplierDocuments_attachableSupplierDocuments_nodes,
} from './__generated__/AttachableSupplierDocuments';
import { AttachSupplierDocumentVariables } from './__generated__/AttachSupplierDocument';
import { _columns } from './columns.data';
import { SupplierAvailableDocumentTypes } from '../../__generated__/SupplierAvailableDocumentTypes';
import { Supplier_supplier } from 'settings/account/supplier/view/__generated__/Supplier';

import ATTACHABLE_SUPPLIER_DOCUMENTS from './AttachableSupplierDocuments.graphql';
import ATTACH_SUPPLIER_DOCUMENT from './AttachSupplierDocument.graphql';
import SUPPLIER_DETAILS from '../../../../Supplier.graphql';

type AttachableDataType =
  AttachableSupplierDocuments_attachableSupplierDocuments_nodes;
export interface AttachFormModalProps {
  availableDocumentTypes: SupplierAvailableDocumentTypes | undefined;
  supplier: Supplier_supplier;
  isDirty: boolean;
}

export const AttachFormModal: React.FC<AttachFormModalProps> = ({
  availableDocumentTypes,
  supplier,
}) => {
  const styles = useStyles();

  const getDocumentTypeOptions = useCallback(
    (data: typeof availableDocumentTypes | undefined) => {
      return (
        data?.supplierAvailableDocumentTypes?.nodes.map((doctype) => ({
          key: doctype.id,
          text: doctype.documentType || '',
          title:
            !supplier?.isDraft && doctype.isAccountingDocument
              ? 'Upload prohibited during/after the approval process'
              : undefined,
          disabled:
            !supplier?.isDraft && doctype.isAccountingDocument
              ? true
              : undefined,
        })) || []
      );
    },
    [supplier]
  );

  const canSelectItem = useCallback(
    (item: AttachableDataType) =>
      supplier?.isDraft ? true : !item._isAccountingDocument!,
    [supplier]
  );

  const transformData = useCallback(
    (data: AttachableSupplierDocuments | undefined) => {
      return data?.attachableSupplierDocuments?.nodes || [];
    },
    []
  );

  const getMutationInput = useCallback(
    (selectedItems: AttachableDataType[], entityId: string) => {
      const entityDocIds = selectedItems.map((item) => item.id);
      return {
        input: {
          supplierId: entityId,
          entityDocumentId: entityDocIds,
        },
      } as AttachSupplierDocumentVariables;
    },
    []
  );

  const onRenderItemColumn = useCallback(
    (
      item?: AttachableDataType,
      _index?: number,
      column?: IColumn,
      openDocumentViewModal?: (state: DocumentViewModalState) => void
    ) => {
      if (!item || !column) return undefined;

      const disableLogic = supplier?.isDraft
        ? false
        : item._isAccountingDocument;

      const fieldContent = item[
        column.fieldName as keyof AttachableDataType
      ] as string | null;

      switch (column.key) {
        case 'fileReference':
          return (
            <Stack className={styles.columnHeight} verticalAlign="center">
              <DocumentDataCallout disabled={disableLogic!} item={item} />
            </Stack>
          );
        case 'documentType':
          return (
            <Stack className={styles.columnHeight} verticalAlign="center">
              <Text className={disableLogic ? styles.disabledText : ''}>
                {item.documentTypes?.documentType}
              </Text>
            </Stack>
          );
        case 'indexAmount':
          return (
            <Stack
              verticalAlign="center"
              className={styles.onrenderColumnStack}
            >
              <Text className={styles.contentColumnAlignRight}>
                {fieldContent}
              </Text>
            </Stack>
          );
        case 'view':
          const viewDocumentVisible =
            item._isProtected! || item._fileViewer !== 'browser';
          return (
            <Stack className={styles.columnHeight} verticalAlign="center">
              <TooltipHost content="View" id="tooltipId">
                <IconButton
                  disabled={viewDocumentVisible}
                  iconProps={{ iconName: 'View' }}
                  onClick={() =>
                    openDocumentViewModal?.({
                      isOpen: true,
                      title: item.fileReference,
                      entityDocumentId: item.id,
                      _fileType: item._fileType!,
                    })
                  }
                />
              </TooltipHost>
            </Stack>
          );
        case 'isoCode':
          return (
            <Stack className={styles.columnHeight} verticalAlign="center">
              <Text className={disableLogic ? styles.disabledText : ''}>
                {item.currency?.isoCode}
              </Text>
            </Stack>
          );
        case '_uploadDate':
          return (
            <Stack className={styles.columnHeight} verticalAlign="center">
              <Text className={disableLogic ? styles.disabledText : ''}>
                {item._uploadDate ? getGlobalDateFormat(item._uploadDate) : ''}
              </Text>
            </Stack>
          );
        case 'indexTransactionDate':
          return (
            <Stack className={styles.columnHeight} verticalAlign="center">
              {item.indexTransactionDate && (
                <Text className={disableLogic ? styles.disabledText : ''}>
                  {dateFormat(dateConvertions(item.indexTransactionDate))}
                </Text>
              )}
            </Stack>
          );
        default:
          return (
            <Stack className={styles.columnHeight} verticalAlign="center">
              <Text className={disableLogic ? styles.disabledText : ''}>
                {fieldContent}
              </Text>
            </Stack>
          );
      }
    },
    [styles, supplier]
  );

  return (
    <GenericAttachForm
      entity={{
        id: supplier.id!,
        isDraft: supplier.isDraft,
      }}
      availableDocumentTypes={availableDocumentTypes}
      attachableDocumentsQuery={ATTACHABLE_SUPPLIER_DOCUMENTS}
      attachDocumentsMutation={ATTACH_SUPPLIER_DOCUMENT}
      detailsQuery={SUPPLIER_DETAILS}
      columns={_columns}
      modalWidth={1350}
      getDocumentTypeOptions={getDocumentTypeOptions}
      transformData={transformData}
      canSelectItem={canSelectItem}
      getMutationInput={getMutationInput}
      onRenderItemColumn={onRenderItemColumn}
      initialQueryVariables={{
        supplierId: supplier.id!,
        first: TABLE_ROWS,
      }}
      toOrderByVariable={toOrderByVariable}
    />
  );
};
